import { faker } from '@faker-js/faker';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import FlagIcon from '@mui/icons-material/Flag';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Chip, IconButton, Menu, MenuItem, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useMemo, useRef, useState } from 'react';
import { useEvent } from 'react-use';
import isColorDark from '../../utils/isColorDark';
import Iconify from '../Iconify';
import KanbanCard from '../KanbanCard';
import EditKanbanColumnSettingsModal from '../EditKanbanColumnSettingsModal';
import KanbanInput from '../KanbanInput';

const formatDate = (date) => {
  return new Date(date).toISOString().split('T')[0];
};

export default function KanbanColumn({
  kanban,
  index,
  column,
  cards,
  filters,
  assigneds,
  priorities,
  cardTags,
  taskTitle,
  createCard,
  clearColumn,
  deleteColumn,
  renameColumn,
}) {
  const theme = useTheme();
  /* Restaurados estados e handlers removidos acidentalmente */
  const [addingCard, setAddingCard] = useState(false);
  const [renamingCard, setRenamingCard] = useState(false);
  const [renameInputValue, setRenameInputValue] = useState(column?.title || '');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [openSettingsModal, setOpenSettingsModal] = useState(false);

  const addCardInputRef = useRef(null);
  const renameCardInputRef = useRef(null);

  const handleClickOutside = (event) => {
    if (addCardInputRef.current && !addCardInputRef.current.contains(event.target)) {
      setAddingCard(false);
    }
    if (renameCardInputRef.current && !renameCardInputRef.current.contains(event.target)) {
      setRenamingCard(false);
    }
  };

  useEvent('click', handleClickOutside);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setDropdownOpen(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setDropdownOpen(false);
  };

  const handleSaveRename = (value) => {
    setRenameInputValue(value);
    renameColumn({ ...column, title: value });
    setRenamingCard(false);
  };

  const handleCreateCard = (value) => {
    createCard(column.id, {
      id: faker.string.uuid(),
      title: value,
      reporter: faker.image.avatarGitHub(),
      priority: 'high',
    });
    setAddingCard(false);
  };

  const handleOpenSettingsModal = (columnId) => {
    setOpenSettingsModal(true);
  };

  /* removido useState e useEffect de filteredCards que estavam causando delay na contagem */
  const filteredCards = useMemo(() => {
    const uniqueCards = () => {
      const unique = [];
      if (!cards) return unique;
      cards.forEach((card) => {
        const existingIndex = unique.findIndex((existingCard) => existingCard.id === card.id);
        if (existingIndex === -1) {
          unique.push(card);
        } else {
          unique[existingIndex] = card;
        }
      });
      return unique;
    };

    let filtered = uniqueCards();

    if (!filters) return filtered;

    return filtered.filter((card) => {
      if (filters?.filterDateBy != null && (filters?.startAt || filters?.endAt)) {
        const { startAt, endAt, filterDateBy } = filters;

        const formattedStartAt = startAt ? formatDate(startAt) : null;
        const formattedEndAt = endAt ? formatDate(endAt) : null;

        const getCardDate = (filterType) => {
          switch (filterType) {
            case "created":
              return card.createdAt ? formatDate(card.createdAt) : null;
            case "started":
              return card.startedAt ? formatDate(card.startedAt) : null;
            case "finished":
              return card.finishedAt ? formatDate(card.finishedAt) : null;
            default:
              return null;
          }
        };

        const cardDate = getCardDate(filterDateBy);

        if (cardDate) {
          if (formattedStartAt && formattedEndAt) {
            if (cardDate < formattedStartAt || cardDate > formattedEndAt) return false;
          } else if (formattedStartAt) {
            if (cardDate !== formattedStartAt) return false;
          } else if (formattedEndAt) {
            if (cardDate !== formattedEndAt) return false;
          }
        }
      }

      const hasPriority =
        filters?.priorityFilter?.length > 0 &&
        filters.priorityFilter.some((priority) => card?.kanbanPriorityId === priority?.id);
      if (filters?.priorityFilter?.length > 0 && !hasPriority) return false;

      const hasTag = filters?.selectedTags?.some(tag => card?.tags?.some(cardTag => cardTag?.id === tag?.id));
      if (filters?.selectedTags?.length > 0 && !hasTag) return false;

      const hasAssignedUser =
        filters?.assignedFilter?.length > 0 &&
        card.assigned.some((assignedUser) =>
          filters.assignedFilter.some((filterUser) => filterUser?.id === assignedUser?.id)
        );
      if (filters?.assignedFilter?.length > 0 && !hasAssignedUser) return false;

      const hasCreator =
        filters?.createdByFilter?.length > 0 && filters.createdByFilter.some((user) => user.id === card.creator.id);
      if (filters?.createdByFilter?.length > 0 && !hasCreator) return false;

      return true;
    });
  }, [cards, filters]);


  return (
    <>
      <Draggable draggableId={column.id} index={index}>
        {(provided) => (
          <Paper
            {...provided.draggableProps}
            ref={provided.innerRef}
            variant="outlined"
            sx={{
              padding: '0px',
              borderRadius: 2,
              borderStyle: 'dashed',
              bgcolor: (theme) => (theme.palette.mode === 'light' ? 'rgb(244 246 248)' : 'rgb(37 44 53)'),
            }}
          >
            <Stack spacing={1}>
              <Box
                sx={{
                  backgroundColor: column.color,
                  width: '350px',
                  maxWidth: '350px',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  userSelect: 'none',
                  padding: '10px 5px 10px 5px',
                  borderRadius: '10px 10px 0px 0px',
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Box
                  sx={{
                    maxWidth: renamingCard ? '100%' : '80%',
                    ...(renamingCard
                      ? {
                        width: '100%',
                      }
                      : {
                        cursor: 'pointer',
                        transition: 'border 0.3s ease',
                        padding: '6px 6px 6px 6px',
                        '&:hover': {
                          padding: '5px 5px 5px 5px',
                          border: (theme) => `1px solid ${column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff')}`,
                          borderRadius: '4px',
                        },
                      }),
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (renamingCard) return;
                    setRenamingCard(true);
                  }}
                >
                  {renamingCard ? (
                    <KanbanInput
                      color={column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff')}
                      ref={renameCardInputRef}
                      label="Titulo"
                      limit={25}
                      value={renameInputValue}
                      onSave={handleSaveRename}
                      onCancel={() => setRenamingCard(false)}
                    />
                  ) : (
                    <Box sx={{ display: 'flex' }}>
                      {filters?.showTaskCount && (
                        <Chip
                          variant="contained"
                          label={filteredCards?.length || 0}
                          size="small"
                          sx={{
                            marginRight: '10px',
                            backgroundColor: (theme) => column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff'),
                            color: (theme) => column?.color ? isColorDark(column.color) ? '#000000' : '#ffffff' : (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                          }}
                        />
                      )}
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        whiteSpace="pre-wrap"
                        sx={{
                          color: (theme) => column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                        }}
                      >
                        {column.title}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box>
                  {!renamingCard && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {isHovered && ( // Exibe o Tooltip apenas se isHovered for true
                        <Tooltip placement="top" arrow title="Mover coluna">
                          <Box {...provided.dragHandleProps}>
                            <IconButton sx={{ padding: '3px', marginRight: '10px', cursor: 'grab' }}>
                              <Iconify icon="material-symbols:ad-group-outline"
                                sx={{
                                  color: (theme) => column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                                }}
                              />
                            </IconButton>
                          </Box>
                        </Tooltip>
                      )}
                      <Tooltip placement="top" arrow title="Opções da coluna">
                        <IconButton sx={{ padding: '0px' }} onClick={handleMenuOpen}>
                          <MoreVertIcon
                            sx={{
                              color: (theme) => column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip placement="top"
                        arrow
                        title="Nova tarefa"
                      >
                        <IconButton
                          sx={{ padding: '0px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddingCard(true);
                          }}
                        >
                          <Iconify
                            icon="material-symbols:add-2-rounded"
                            sx={{
                              color: (theme) => column?.color ? isColorDark(column.color) ? '#ffffff' : '#000000' : (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                      {column?.hasStart && (
                        <Tooltip
                          placement="top"
                          arrow
                          title="Ao mover para esta coluna, a tarefa será marcada com a data de início."
                        >
                          <FlagIcon sx={{ color: '#23c803', fontSize: 20 }} />
                        </Tooltip>
                      )}

                      {column?.hasFinished && (
                        <Tooltip
                          placement="top"
                          arrow
                          title="Ao mover para esta coluna, a tarefa será marcada com a data de finalização."
                        >
                          <FlagIcon sx={{ color: '#c80303', fontSize: 20 }} />
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  <Menu anchorEl={anchorEl} open={dropdownOpen} onClose={handleMenuClose}>
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenSettingsModal(column.id);
                        handleMenuClose();
                      }}
                      sx={{ color: 'grey.600', display: 'flex', alignItems: 'center' }}
                    >
                      <Iconify icon="solar:settings-bold-duotone" />
                      <Box sx={{ ml: 1 }}>Configurar Ações</Box>
                    </MenuItem>
                    {/*
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        clearColumn(column.id);
                        handleMenuClose();
                      }}
                      sx={{ color: 'grey.600', display: 'flex', alignItems: 'center' }}
                    >
                      <Iconify icon="solar:eraser-bold" />
                      <Box sx={{ ml: 1 }}>Limpar coluna</Box>
                    </MenuItem>
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteColumn(column.id);
                        handleMenuClose();
                      }}
                      sx={{ color: 'warning.main', display: 'flex', alignItems: 'center' }}
                    >
                      <Iconify icon="solar:archive-down-bold" />
                      <Box sx={{ ml: 1 }}>Arquivar coluna</Box>
                    </MenuItem> 
                    */}
                  </Menu>
                </Box>
              </Box>
              {addingCard && (
                <Container $themeMode={theme.palette.mode}>
                  <Box sx={{ width: '100%', padding: '10px' }}>
                    <KanbanInput
                      ref={addCardInputRef}
                      label="Titulo da tarefa"
                      limit={1000}
                      value={''}
                      onSave={handleCreateCard}
                      onCancel={() => setAddingCard(false)}
                    />
                  </Box>
                </Container>
              )}
              <Droppable droppableId={column.id} index={index} type="TASK" key={index}>
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'rgb(244 246 248)' : 'rgb(37 44 53)'),
                      minHeight: '5px',
                      padding: '10px',
                      maxHeight: 'calc(100vh - 300px)',
                      height: 'calc(100vh - 300px)',
                      overflowY: 'auto',
                    }}
                  >
                    {filteredCards.map((card, index) => (
                      <KanbanCard
                        filters={filters}
                        columnId={column.id}
                        column={column}
                        card={card}
                        assigneds={assigneds}
                        priorities={priorities}
                        cardTags={cardTags}
                        taskTitle={taskTitle}
                        key={card.id}
                        index={index}
                      />
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Stack>
          </Paper>
        )}
      </Draggable>
      <EditKanbanColumnSettingsModal
        kanban={kanban}
        columnId={column.id}
        index={index}
        open={openSettingsModal}
        onClose={setOpenSettingsModal}
      />
    </>
  );
}

const Container = styled(Box)(({ $themeMode }) => ({
  width: 320,
  borderRadius: 12,
  padding: '5px 12px 12px 12px',
  marginBottom: 10,
  fontWeight: 400,
  fontSize: 12,
  backgroundColor: $themeMode === 'light' ? 'rgb(255, 255, 255)' : 'rgb(22, 28, 36)',
  backdropFilter: 'none',
  '&:hover': {
    boxShadow:
      $themeMode === 'light'
        ? 'rgba(145, 158, 171, 0.16) 0px 20px 40px -4px'
        : 'rgba(0, 0, 0, 0.16) 0px 20px 40px -4px',
  },
}));
